(() => {
  const root = document.documentElement;
  const iconsAttribute = "data-mindelor-transparent-header-icons";
  const transparentTargetSelector = "main > .shopify-section:first-child [allow-transparent-header], .shopify-section:first-child [allow-transparent-header]";
  const desktopPointerQuery = window.matchMedia("screen and (pointer: fine)");
  const closePermissionDuration = 750;

  const parseRgbColors = (value) => {
    if (!value || value === "none") {
      return [];
    }

    return Array.from(value.matchAll(/rgba?\(([^)]+)\)/gi)).map((match) => {
      const parts = match[1].split(/,\s*|\s+\/\s*|\s+/).filter(Boolean).map((part) => part.trim());
      const [r, g, b] = parts;
      const alpha = parts[3] === undefined ? 1 : Number(parts[3]);

      return {
        r: Number(r),
        g: Number(g),
        b: Number(b),
        a: Number.isFinite(alpha) ? alpha : 1
      };
    }).filter((color) => [color.r, color.g, color.b].every(Number.isFinite));
  };

  const luminance = ({ r, g, b }) => {
    const [red, green, blue] = [r, g, b].map((channel) => {
      const normalized = channel / 255;

      return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
    });

    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
  };

  const sampleImageLuminance = (image) => {
    if (!image?.complete || image.naturalWidth === 0 || image.naturalHeight === 0) {
      return null;
    }

    try {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d", { willReadFrequently: true });

      canvas.width = 8;
      canvas.height = 8;
      context.drawImage(image, 0, 0, image.naturalWidth, Math.max(1, image.naturalHeight * 0.35), 0, 0, 8, 8);

      const pixels = context.getImageData(0, 0, 8, 8).data;
      let total = 0;

      for (let index = 0; index < pixels.length; index += 4) {
        total += luminance({ r: pixels[index], g: pixels[index + 1], b: pixels[index + 2] });
      }

      return total / (pixels.length / 4);
    } catch (error) {
      return null;
    }
  };

  const getActiveSurface = (target) => {
    if (!target) {
      return null;
    }

    return target.querySelector(".slideshow__slide.is-selected") || target;
  };

  const getComputedColors = (surface) => {
    const elements = [
      surface,
      surface?.querySelector(".content-over-media"),
      surface?.querySelector(".slideshow__slide-content"),
      surface?.querySelector(".content"),
      surface?.querySelector(".prose")
    ].filter(Boolean);

    return elements.flatMap((element) => {
      const style = window.getComputedStyle(element);

      return [
        ...parseRgbColors(style.getPropertyValue("--content-over-media-gradient-overlay")),
        ...parseRgbColors(style.getPropertyValue("--content-over-media-overlay")),
        ...parseRgbColors(style.backgroundColor)
      ];
    });
  };

  const shouldUseDarkHeaderIcons = (target) => {
    const surface = getActiveSurface(target);

    if (!surface) {
      return false;
    }

    const content = surface.querySelector(".slideshow__slide-content, .content, .prose");
    const contentTextColor = parseRgbColors(window.getComputedStyle(content || surface).color)[0];

    if (contentTextColor && luminance(contentTextColor) < 0.45) {
      return true;
    }

    const colors = getComputedColors(surface);
    const hasDarkOverlay = colors.some((color) => color.a >= 0.24 && luminance(color) < 0.22);
    const hasLightSurface = colors.some((color) => color.a >= 0.5 && luminance(color) > 0.7);

    if (hasDarkOverlay) {
      return false;
    }

    if (hasLightSurface) {
      return true;
    }

    const imageLuminance = sampleImageLuminance(surface.querySelector("picture img, img"));

    if (imageLuminance !== null) {
      return imageLuminance > 0.52;
    }

    return Boolean(surface.querySelector("picture, img, video, video-media"));
  };

  const syncHeaderIconContrast = () => {
    const target = document.querySelector(transparentTargetSelector);

    if (!root.hasAttribute("data-initial-transparent-header") && !target) {
      root.removeAttribute(iconsAttribute);
      return;
    }

    root.setAttribute(iconsAttribute, shouldUseDarkHeaderIcons(target) ? "dark" : "light");
  };

  let contrastFrame = 0;

  const requestContrastSync = () => {
    if (contrastFrame) {
      return;
    }

    contrastFrame = window.requestAnimationFrame(() => {
      contrastFrame = 0;
      syncHeaderIconContrast();
    });
  };

  // Keep transparent header icons readable on light first sections without changing solid header state.
  const setupHeaderContrast = () => {
    const target = document.querySelector(transparentTargetSelector);

    syncHeaderIconContrast();

    if (!target) {
      return;
    }

    target.querySelectorAll("img").forEach((image) => {
      if (!image.complete) {
        image.addEventListener("load", requestContrastSync, { once: true });
      }
    });

    new MutationObserver(requestContrastSync).observe(target, {
      attributes: true,
      subtree: true,
      attributeFilter: ["class", "style"]
    });

    window.addEventListener("resize", requestContrastSync, { passive: true });
  };

  const isCartDrawer = (drawer) => drawer?.matches?.("cart-drawer");
  const getCartDrawer = () => document.querySelector("cart-drawer");

  const allowAndHideCartDrawer = (drawer) => {
    if (!isCartDrawer(drawer) || !drawer.open) {
      return;
    }

    allowNextCartClose(drawer);
    drawer.hide();
  };

  const hasCartClosePermission = (drawer) => {
    return Boolean(
      drawer?._mindelorAllowClose &&
      drawer?._mindelorAllowCloseUntil > window.performance.now()
    );
  };

  const clearCartClosePermission = (drawer) => {
    drawer._mindelorAllowClose = false;
    drawer._mindelorAllowCloseUntil = 0;
    drawer._allowNextHide = false;
  };

  const allowNextCartClose = (drawer) => {
    window.clearTimeout(drawer._mindelorAllowCloseTimer);
    drawer._mindelorAllowClose = true;
    drawer._mindelorAllowCloseUntil = window.performance.now() + closePermissionDuration;
    drawer._allowNextHide = true;
    drawer._mindelorAllowCloseTimer = window.setTimeout(() => {
      if (drawer._mindelorAllowCloseUntil <= window.performance.now()) {
        clearCartClosePermission(drawer);
      }
    }, closePermissionDuration);
  };

  const eventPathIncludesCartContent = (drawer, event) => {
    const path = event.composedPath?.() || [];
    const content = drawer.getShadowPartByName?.("content");

    return Boolean(
      content && path.includes(content) ||
      event.target instanceof Node && event.target !== drawer && drawer.contains(event.target)
    );
  };

  const eventPathIncludesCartControl = (drawer, event) => (event.composedPath?.() || []).some((node) => {
    return node instanceof Element && node.getAttribute("aria-controls") === drawer.id;
  });

  // Desktop fallback only: mobile drawer gestures and header search persistence keep their existing behavior.
  const setupCartDrawerCloseFallback = () => {
    document.addEventListener("pointerdown", (event) => {
      const drawer = getCartDrawer();

      if (!desktopPointerQuery.matches || !drawer?.open) {
        return;
      }

      if (eventPathIncludesCartControl(drawer, event)) {
        allowNextCartClose(drawer);
        return;
      }

      if (!eventPathIncludesCartContent(drawer, event)) {
        allowAndHideCartDrawer(drawer);
      }
    }, true);

    document.addEventListener("keydown", (event) => {
      const drawer = getCartDrawer();

      if (!desktopPointerQuery.matches || !drawer?.open || !["Escape", "Esc"].includes(event.key)) {
        return;
      }

      allowAndHideCartDrawer(drawer);
    }, true);

    window.customElements.whenDefined("cart-drawer").then(() => {
      const CartDrawer = window.customElements.get("cart-drawer");

      if (CartDrawer && !CartDrawer.prototype._mindelorExplicitClosePatched) {
        const originalHide = CartDrawer.prototype.hide;
        const originalHideForOutsideClickTarget = CartDrawer.prototype.hideForOutsideClickTarget;

        CartDrawer.prototype._mindelorExplicitClosePatched = true;
        CartDrawer.prototype.hide = function(...args) {
          if (desktopPointerQuery.matches && this.open && !hasCartClosePermission(this)) {
            clearCartClosePermission(this);
            document.querySelector("x-header")?.forceVisibility?.(true);
            return Promise.resolve();
          }

          const hideResult = originalHide.apply(this, args);
          Promise.resolve(hideResult).finally(() => clearCartClosePermission(this));

          return hideResult;
        };

        CartDrawer.prototype.hideForOutsideClickTarget = function(target) {
          const shouldHide = originalHideForOutsideClickTarget.call(this, target);

          if (shouldHide) {
            allowNextCartClose(this);
          }

          return shouldHide;
        };
      }

      const drawer = getCartDrawer();
      const overlay = drawer?.getShadowPartByName?.("overlay");

      drawer?.addEventListener("dialog:force-close", () => allowNextCartClose(drawer), { capture: true });
      overlay?.addEventListener("click", () => allowAndHideCartDrawer(drawer), { capture: true });
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setupHeaderContrast, { once: true });
  } else {
    setupHeaderContrast();
  }

  setupCartDrawerCloseFallback();
})();
